import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { map } from 'rxjs';
import { AUTH_CONFIG, AuthResp } from './auth.config';
import { StorageService } from './storage.service';
import { TokenStore } from './token.store';

/**
 * Two jobs:
 *  - on a sign-in or refresh response, hand both tokens to the TokenStore (expiry = the token's own)
 *  - on every API call from a signed-in user, add the `access_token` header (never Authorization: Bearer)
 */
export const tokenCachingInterceptor: HttpInterceptorFn = (req, next) => {
  const config = inject(AUTH_CONFIG);
  const storage = inject(StorageService);
  const tokens = inject(TokenStore);

  const isAuthCall = req.url.includes(config.signInPath) || req.url.includes(config.refreshPath);
  if (isAuthCall) {
    return next(req).pipe(
      map(event => {
        if (event instanceof HttpResponse && event.ok) {
          const body = event.body as AuthResp;
          // identity sends seconds; the store and Date want ms. mutated in place on purpose:
          // callers downstream (the sign-in page) read the same object and expect ms. pre-existing contract.
          body.accessToken.expiresAt *= 1000;
          body.refreshToken.expiresAt *= 1000;
          tokens.set(body.accessToken, body.refreshToken);
          // the user object rides on both doors. The sign-in page saves it on sign-in; a refresh
          // saves it here, so what identity puts on it later (a promotion, `effectiveRoles`)
          // reaches the stored user without a new sign-in (task-adopt-effective-roles.md).
          const user = (body as unknown as Record<string, unknown>)[config.userField];
          if (req.url.includes(config.refreshPath) && user) storage.saveUser(user);
        }
        return event;
      }),
    );
  }

  if (req.url.includes('/logout')) {
    tokens.clear();
    return next(req);
  }

  const accessToken = tokens.accessToken();
  const isLoggedIn = storage.getUser() != null && accessToken != null;
  if (isLoggedIn && req.url.startsWith(config.baseUrl)) {
    req = req.clone({ setHeaders: { access_token: accessToken.token } });
  }
  return next(req);
};
